/* eslint-disable no-nested-ternary */
import React, { useEffect } from 'react';
import { ChevronLeft, Loader } from 'react-feather';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import QuillEditor from '@/shared/components/UI/QuillEditor';
import { useBasicInfo } from './hooks/useBasicInfo';

import ProductOptions from './components/UI/ProductOptions';
import ProductSidebar from './components/UI/ProductSidebar';
import ThumbnailUpload from './components/UI/ThumbnailUpload';
import { useProductFormStore } from './store/productFormStore';

const ProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isCreateMode = location.pathname.includes('create');

  const {
    formData,
    isLoading,
    isSaving,
    error,
    loadProduct,
    resetForm,
    setAddMode,
    saveProduct,
    setNavigate,
    setStatus,
  } = useProductFormStore();

  const {
    basicInfo,
    handleTitleChange,
    handleDescriptionChange,
    handleSKUChange,
    handleHsCodeChange,
    handleThumbnailsChange,
  } = useBasicInfo();

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate, setNavigate]);

  useEffect(() => {
    if (isCreateMode) {
      resetForm();
      setAddMode();
    } else if (productId) {
      loadProduct(productId);
    }
  }, [isCreateMode, productId, loadProduct, resetForm, setAddMode]);

  const handleStatusChange = (statusName: string) => {
    setStatus(statusName);
  };

  const handleSaveProduct = async () => {
    await saveProduct(isCreateMode, productId);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F1F1F1]">
        <Loader size={32} className="animate-spin text-[#95699F]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F1F1F1]">
        <div className="text-center">
          <p className="text-lg font-medium text-red-600">
            Failed to load product details.
          </p>
          <p className="mt-2 text-sm text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F1F1]">
      <div className="px-8 pb-12 pt-6">
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="flex cursor-pointer items-center gap-3"
            onClick={() => window.history.back()}
          >
            <ChevronLeft size={24} className="text-[#888888]" />
            <h2 className="text-2xl font-bold leading-8 text-[#1D1D1D]">
              {isCreateMode ? 'Create Product' : 'Edit Product'}
            </h2>
          </button>
          <div className="flex gap-2">
            <select
              name="product-active"
              id="product-active"
              className="h-8 w-24 rounded border-none bg-[#E2E2E2] px-2 py-1 text-sm font-medium outline-none focus:outline-none"
              value={formData.basicInfo.status.statusName}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="closed">Closed</option>
              <option value="draft">Draft</option>
            </select>
            <button
              type="button"
              className="h-8 cursor-pointer rounded bg-[#95699F] px-3 py-1 text-sm font-normal text-white hover:bg-[#B995BE] disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleSaveProduct}
              disabled={isSaving}
            >
              {isSaving ? (
                <div className="flex items-center gap-2">
                  <Loader size={12} className="animate-spin" />
                  {isCreateMode ? 'Creating...' : 'Saving...'}
                </div>
              ) : isCreateMode ? (
                'Create'
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>

        <section className="mt-8 flex w-full gap-5">
          <div className="flex w-[68%] flex-col gap-5">
            <div className="flex flex-col gap-5 rounded border border-[#F1F1F1] bg-white p-5 shadow-sm">
              <div>
                <h2 className="text-base font-medium">Title</h2>
                <input
                  type="text"
                  placeholder="title"
                  className="mt-3 h-10 w-full rounded border border-[#CECECE] px-3 text-base font-normal text-[#222] outline-none placeholder:text-[#999]"
                  value={basicInfo.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                />
              </div>

              <ThumbnailUpload
                thumbnailUrls={basicInfo.thumbnailUrlArray}
                onThumbnailsChange={handleThumbnailsChange}
                disabled={false}
              />

              <div>
                <h2 className="mb-2 text-base font-medium">Description</h2>
                <div>
                  <QuillEditor
                    value={basicInfo.description}
                    onChange={handleDescriptionChange}
                  />
                </div>
              </div>
            </div>

            <ProductOptions productId={productId} />

            <div className="flex flex-col gap-5 rounded border border-[#F1F1F1] bg-white p-5 shadow-sm">
              <div>
                <label htmlFor="sku" className="text-base font-medium">
                  SKU (Stock Keeping Unit)
                </label>
                <input
                  type="text"
                  className="mt-2 w-full rounded border border-[#CECECE] px-3 py-2 outline-none"
                  value={basicInfo.SKU || ''}
                  onChange={(e) => handleSKUChange(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="hs-code" className="text-base font-medium">
                  Harmonized System (HS) code{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="mt-2 w-full rounded border border-[#CECECE] px-3 py-2 outline-none"
                  value={basicInfo.hsCode}
                  onChange={(e) => handleHsCodeChange(e.target.value)}
                />
              </div>
            </div>
          </div>

          <ProductSidebar />
        </section>
      </div>
    </div>
  );
};

export default ProductDetailPage;
